<template>
  <div class="slack-integration">
    <div class="si-header">
      <div class="si-icon"><i class="mdi mdi-slack"></i></div>
      <div class="si-heading">
        <h5 class="title si-title">Slack Integration</h5>
        <p class="si-subtitle">Post admin activities (settings changes, approvals, user updates) to a Slack channel.</p>
      </div>
      <span v-if="!loading" class="si-status" :class="statusClass">{{ statusLabel }}</span>
    </div>

    <div v-if="loading" class="si-loading">Loading…</div>

    <div v-else class="si-body">
      <label class="si-label" for="slackWebhook">Incoming Webhook URL</label>
      <div class="si-input-wrap">
        <i class="fa fa-link"></i>
        <input v-if="showUrl" id="slackWebhook" type="text" class="si-input" v-model.trim="form.webhookUrl"
          placeholder="https://hooks.slack.com/services/..." autocomplete="off" />
        <input v-else id="slackWebhook" type="password" class="si-input" v-model.trim="form.webhookUrl"
          placeholder="https://hooks.slack.com/services/..." autocomplete="off" />
        <button type="button" class="si-eye" :title="showUrl ? 'Hide' : 'Show'" @click="showUrl = !showUrl">
          <i :class="showUrl ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
        </button>
      </div>
      <small class="si-help">
        In Slack, create an app with <b>Incoming Webhooks</b> turned on, add a webhook to the channel you want, and paste its URL here.
        <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener">How to get a webhook URL</a>
      </small>

      <label class="si-toggle">
        <input type="checkbox" v-model="form.enabled" />
        <span class="si-slider"></span>
        <span class="si-toggle-text">Send activities to Slack</span>
      </label>
    </div>

    <div v-if="!loading" class="si-footer">
      <button v-if="saved.webhookUrl" type="button" class="si-btn si-btn--danger" :disabled="busy" @click="disconnect">
        Disconnect
      </button>
      <span class="si-spacer"></span>
      <button type="button" class="si-btn si-btn--ghost" :disabled="busy || !webhookValid" @click="sendTest">
        {{ testing ? "Sending…" : "Send Test Message" }}
      </button>
      <button type="button" class="si-btn si-btn--primary" :disabled="busy || !isDirty" @click="save">
        {{ saving ? "Saving…" : "Save" }}
      </button>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert";
import {
  getSlackConfig,
  saveSlackConfig,
  removeSlackConfig,
  isValidSlackWebhook,
  postToSlack,
} from "../../../utils/slack";
import { createActivityPayload, logAdminActivity } from "../../../utils/adminLogs";

const EMPTY = { webhookUrl: "", enabled: true };

export default {
  name: "SlackIntegration",
  data() {
    return {
      loading: true,
      saving: false,
      testing: false,
      showUrl: false,
      saved: { ...EMPTY },
      form: { ...EMPTY },
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user || {};
    },
    busy() {
      return this.saving || this.testing;
    },
    webhookValid() {
      return isValidSlackWebhook(this.form.webhookUrl);
    },
    isDirty() {
      return this.form.webhookUrl !== this.saved.webhookUrl || this.form.enabled !== this.saved.enabled;
    },
    statusLabel() {
      if (!this.saved.webhookUrl) return "Not connected";
      return this.saved.enabled ? "Active" : "Paused";
    },
    statusClass() {
      if (!this.saved.webhookUrl) return "is-off";
      return this.saved.enabled ? "is-on" : "is-paused";
    },
  },
  async mounted() {
    try {
      const config = await getSlackConfig();
      this.saved = {
        webhookUrl: config.webhookUrl || "",
        enabled: config.webhookUrl ? config.enabled !== false : true,
      };
      this.form = { ...this.saved };
    } catch (error) {
      console.error("Failed to load Slack settings:", error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async sendTest() {
      if (!this.webhookValid) return;
      this.testing = true;
      try {
        await postToSlack(this.form.webhookUrl, {
          text: `:white_check_mark: Pertime is connected. Admin activities for ${this.companyName()} will appear in this channel.`,
        });
        Swal("Test sent", "Check your Slack channel. If nothing arrives, the webhook URL is wrong or was revoked.", "info");
      } catch (error) {
        Swal("Error!", "Could not reach Slack. Check your connection and try again.", "error");
      } finally {
        this.testing = false;
      }
    },
    async save() {
      if (this.form.webhookUrl && !this.webhookValid) {
        Swal("Invalid URL", "The webhook URL should start with https://hooks.slack.com/services/", "error");
        return;
      }
      if (!this.form.webhookUrl) {
        await this.disconnect();
        return;
      }
      this.saving = true;
      try {
        await saveSlackConfig({
          webhookUrl: this.form.webhookUrl,
          enabled: this.form.enabled,
          updatedBy: this.currentUser.uid || null,
          updatedAt: new Date().toISOString(),
        });
        const wasConnected = !!this.saved.webhookUrl;
        this.saved = { ...this.form };
        Swal("Success!", "Slack settings saved!", "success");
        await this.logChange(
          wasConnected ? "updated Slack integration" : "connected Slack",
          `Slack activity notifications ${this.form.enabled ? "enabled" : "paused"}.`
        );
      } catch (error) {
        console.error("Failed to save Slack settings:", error);
        Swal("Error!", "Failed to save Slack settings.", "error");
      } finally {
        this.saving = false;
      }
    },
    async disconnect() {
      const confirmed = await Swal({
        title: "Disconnect Slack?",
        text: "Activities will no longer be posted to Slack.",
        icon: "warning",
        buttons: ["Cancel", "Disconnect"],
        dangerMode: true,
      });
      if (!confirmed) return;
      this.saving = true;
      try {
        // Log first so the disconnect itself still reaches Slack
        await this.logChange("disconnected Slack", "Slack activity notifications turned off.");
        await removeSlackConfig();
        this.saved = { ...EMPTY };
        this.form = { ...EMPTY };
        Swal("Disconnected", "Slack has been disconnected.", "success");
      } catch (error) {
        console.error("Failed to disconnect Slack:", error);
        Swal("Error!", "Failed to disconnect Slack.", "error");
      } finally {
        this.saving = false;
      }
    },
    companyName() {
      const company = this.$store.state.auth.company;
      return (company && company.companyName) || "your company";
    },
    async logChange(action, description) {
      try {
        await logAdminActivity(
          createActivityPayload({
            user: this.currentUser,
            title: `${this.currentUser.username} ${action}`,
            description,
            type: "settings",
          })
        );
      } catch (error) {
        console.warn("Failed to log Slack settings change:", error);
      }
    },
  },
};
</script>

<style scoped>
.slack-integration {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px 24px;
  background: #fff;
}

.si-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.si-icon {
  flex: 0 0 42px;
  height: 42px;
  border-radius: 10px;
  background: #4a154b;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.si-heading {
  flex: 1;
}

.si-title {
  margin: 0;
}

.si-subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
}

.si-status {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.si-status.is-on {
  background: #dcfce7;
  color: #166534;
}

.si-status.is-paused {
  background: #fef3c7;
  color: #92400e;
}

.si-status.is-off {
  background: #f3f4f6;
  color: #6b7280;
}

.si-loading {
  margin-top: 16px;
  color: #6b7280;
  font-size: 13px;
}

.si-body {
  margin-top: 20px;
}

.si-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.si-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.si-input-wrap > i {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  font-size: 14px;
  pointer-events: none;
}

.si-input {
  width: 100%;
  height: 40px;
  padding: 0 40px 0 36px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.si-input:focus {
  outline: none;
  border-color: #435851;
  box-shadow: 0 0 0 3px rgba(67, 88, 81, 0.15);
}

.si-eye {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
}

.si-help {
  display: block;
  margin-top: 6px;
  color: #9ca3af;
  font-size: 12px;
  line-height: 1.5;
}

.si-help a {
  color: #435851;
  font-weight: 600;
}

/* Toggle */
.si-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  cursor: pointer;
  user-select: none;
}

.si-toggle input {
  display: none;
}

.si-slider {
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: #d1d5db;
  transition: background 0.2s;
}

.si-slider::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}

.si-toggle input:checked + .si-slider {
  background: #435851;
}

.si-toggle input:checked + .si-slider::after {
  transform: translateX(16px);
}

.si-toggle-text {
  font-size: 14px;
  color: #374151;
}

/* Footer */
.si-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid #f1f1f1;
  flex-wrap: wrap;
}

.si-spacer {
  flex: 1;
}

.si-btn {
  min-width: 100px;
  height: 38px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: opacity 0.2s;
}

.si-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.si-btn--primary {
  background: #435851;
  color: #fff;
}

.si-btn--ghost {
  background: #fff;
  color: #374151;
  border-color: #d1d5db;
}

.si-btn--danger {
  background: #fff;
  color: #dc2626;
  border-color: #fecaca;
}

@media (max-width: 576px) {
  .si-header {
    flex-wrap: wrap;
  }

  .si-spacer {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/SlackIntegration.vue